import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import EditView from './edit-view';
import ReadView from './read-view';
import type { InlineEditLiteProps } from './types';
import useButtonFocusHook from './utils';

const actionSubject = 'inlineEdit';
/**
 * This component is a lightweight alternative to `@atlaskit/inline-edit` that is suitable to use in experiences with
 * **many** inline edit components rendered concurrently, e.g. in a table-based layout. The main advantage of this
 * alternative is that it doesn't render any additional DOM elements while in the read view, whereas by default
 * `@atlaskit/inline-edit` renders 4 wrapper elements, including an `@atlaskit/form` field which can lead to performance
 * regression if used heavily throughout the UI.
 *
 * In addition, there are some minor changes to the component API that allow more flexibility/responsibility from the
 * consumer when rendering, key changes are:
 * - Stateless `isEditing` - `isEditing` is a controlled-only prop that must always be provided by the consumer.
 * - No label - Any form labels present in the UI must be rendered manually in the `readView`/`editView` components.
 * - Read view accessibility - Consumers must render their own buttons and wrapper elements as needed to construct an
 * accessible UI. Note that the `readView` prop now accepts an additional argument that includes props the consumer can
 * spread onto the underlying DOM elements to provide an accessible experience.
 *
 * Further optimisations could be made, specifically in the `editView` path, to achieve a lighter bundle, e.g.
 * entrypoints (load on intent), making form rendering a consumer responsibility etc. However, these are considered less
 * impactful on page rendering performance as most use cases will render the `readView` by default, therefore it's
 * currently out of scope of this component.
 *
 * **Note** - Adopters should prefer `@atlaskit/inline-edit` for the most up to date and platform-friendly solution, and
 * should only consider adopting this component if they've explicitly identified a negative impact on performance from
 * the design system solution.
 */
function InlineEditLite<FieldValue>(props: InlineEditLiteProps<FieldValue>) {
	const {
		cancelButtonLabel,
		confirmButtonLabel,
		defaultValue,
		editButtonLabel,
		editView,
		hideActionButtons,
		isEditing,
		isRequired,
		keepEditViewOpenOnBlur,
		onCancel,
		onConfirm,
		onEdit,
		readView,
		validate,
	} = props;

	const { editButtonRef, doNotFocusOnEditButton } = useButtonFocusHook(isEditing);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCancelRequest = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'canceled',
			actionSubject,
		});
		onCancel(analyticsEvent);
	}, [createAnalyticsEvent, onCancel]);

	const onConfirmRequest = useCallback(
		(value: FieldValue) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'confirmed',
				actionSubject,
			});
			onConfirm(value, analyticsEvent);
		},
		[createAnalyticsEvent, onConfirm],
	);

	const onEditRequest = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'focused',
			actionSubject,
		});
		onEdit(analyticsEvent);
	}, [createAnalyticsEvent, onEdit]);

	if (isEditing) {
		return (
			<EditView
				cancelButtonLabel={cancelButtonLabel}
				confirmButtonLabel={confirmButtonLabel}
				defaultValue={defaultValue}
				editView={editView}
				hideActionButtons={hideActionButtons}
				isRequired={isRequired}
				keepEditViewOpenOnBlur={keepEditViewOpenOnBlur}
				onCancel={onCancelRequest}
				onConfirm={onConfirmRequest}
				onDoNotFocusOnEditButton={doNotFocusOnEditButton}
				validate={validate}
			/>
		);
	}

	return (
		<ReadView
			editButtonLabel={editButtonLabel}
			editButtonRef={editButtonRef}
			onEdit={onEditRequest}
			postReadViewClick={doNotFocusOnEditButton}
			readView={readView}
		/>
	);
}

export default InlineEditLite;

export { EditButton } from './edit-button';
export { EditButtonHidden } from './edit-button-hidden';
export { Form } from './form';
export { useInlineEdit } from './use-inline-edit';
export { useInlineEditReadViewContainer } from './use-inline-edit-read-view-container';
